/** Foundry-compatible Control Panel route construction.
 *
 * The enrollment and organization RIDs are deployment hydration, not Cognito
 * claims. Keeping them separate prevents the URL contract from leaking into
 * IronCase's authorization model (where `agencyId` remains the Cedar tenant
 * boundary).
 */

import type { AppConfig } from "../hex/domain/config.js";
import type { ExtensionType } from "../packages/control-panel-shell/src/registry/ExtensionDefinition.js";

export const CONTROL_PANEL_ROOT = "/workspace/control-panel";

export type ControlPanelRouteScope = "enrollment" | "organization" | "space";

function ridFor(config: AppConfig, scope: ControlPanelRouteScope): string {
  if (scope === "organization") return config.controlPanelOrganizationRid;
  // IronCase does not expose a space-scoped Control Panel yet. Foundry's
  // global extensions are hosted in the active enrollment context.
  return config.controlPanelEnrollmentRid;
}

export function scopeForExtension(type: ExtensionType): ControlPanelRouteScope {
  return type === "organization" ? "organization" : "enrollment";
}

export function controlPanelPath(
  config: AppConfig,
  scope: ControlPanelRouteScope,
  route: string,
): string {
  const cleanRoute = route.replace(/^\/+|\/+$/g, "");
  const base = `${CONTROL_PANEL_ROOT}/${scope}/${encodeURIComponent(ridFor(config, scope))}`;
  return cleanRoute === "" ? base : `${base}/${cleanRoute}`;
}

export function controlPanelExtensionPath(
  config: AppConfig,
  type: ExtensionType,
  route: string,
): string {
  return controlPanelPath(config, scopeForExtension(type), route);
}
